/* M1 Dock: quantity-based preorder gifts and optional bag accessories. */
(() => {
  'use strict';
  if (window.NexDock) return;
  const script = document.querySelector('[data-m1-dock-config]');
  if (!script) return;
  let config;
  try { config = JSON.parse(script.textContent); } catch { return; }
  const bagIds = new Set(config.bags.map(bag => String(bag.id)).filter(Boolean));
  const idOf = item => String(item.variant_id || item.id || '');
  const isDock = item => idOf(item) === config.id;
  const root = config.root || '/';
  const selectedCard = host => host?.querySelector('.gdab__card[aria-checked="true"], .hero-purchase__color[aria-checked="true"]');
  let host, observer, checked = false, checkedVariant = '', pendingPurchase = null, cartRepair = null;
  let preview = null;
  let purchaseBusy = false, cartChanging = false;
  const recoveryKey = 'nex-dock-cart-v2:' + root;
  let recovery = {};
  try { recovery = JSON.parse(sessionStorage.getItem(recoveryKey) || '{}'); } catch { /* Storage can be disabled. */ }
  let discountRestore = Array.isArray(recovery.discounts) ? recovery.discounts : null;
  pendingPurchase = recovery.purchase || null;
  let migration = recovery.migration || null;
  let cartToken = recovery.cartToken || null;
  const saveRecovery = () => {
    try {
      if (discountRestore || pendingPurchase || migration) sessionStorage.setItem(recoveryKey,
        JSON.stringify({ discounts: discountRestore, purchase: pendingPurchase, migration, cartToken }));
      else sessionStorage.removeItem(recoveryKey);
    } catch { /* The current page still keeps its retry guard. */ }
  };
  const bindCart = cart => {
    if (!cart.token) return;
    if (cartToken && cartToken !== cart.token) {
      discountRestore = null;
      pendingPurchase = null;
      migration = null;
    }
    cartToken = cart.token;
    saveRecovery();
  };
  const originals = new WeakMap();
  const money = cents => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency', currency: config.currency, maximumFractionDigits: cents % 100 ? 2 : 0
  }).format(cents / 100);
  const readOnly = () => Boolean(preview || window.NexPurchaseReview);
  const dockAvailable = () => preview ? preview.dock !== 'soldout' : config.available;
  const isGift = item => isDock(item) && item.properties?._m1_dock_offer === 'preorder-gift';
  const giftFor = surface => {
    const card = selectedCard(surface);
    const bag = config.bags.find(item => String(item.id) === card?.dataset.variantId);
    return Boolean(config.giftCheckoutEnabled && config.giftOfferEnabled && dockAvailable()
      && bag?.available && bag.preorder
      && card?.dataset.inventoryAvailable === 'true' && card.dataset.preorder === 'true');
  };
  const eligible = surface => dockAvailable() && selectedCard(surface)?.dataset.inventoryAvailable === 'true'
    && bagIds.has(selectedCard(surface)?.dataset.variantId)
    && selectedCard(surface)?.dataset.preorder !== 'true';
  const surfaces = () => [...document.querySelectorAll('nex-gdab, nex-hero-purchase')];
  const surfaceFor = node => node.closest('nex-gdab, nex-hero-purchase')
    || node.closest('.hero')?.querySelector('nex-hero-purchase');
  const selectionFor = surface => checked && checkedVariant === selectedCard(surface)?.dataset.variantId && eligible(surface);
  const showError = (surface, message) => {
    const error = surface?.querySelector('.gdab__checkout-error, .hero-purchase__error');
    if (error) { error.textContent = message; error.hidden = false; }
    surface?.announce?.(message);
  };
  async function request(path, options = {}) {
    if (readOnly() && options.method && options.method !== 'GET') throw new Error(config.copy.preview);
    const controller = new AbortController();
    const cancel = () => controller.abort();
    options.signal?.addEventListener('abort', cancel, { once: true });
    if (options.signal?.aborted) controller.abort();
    const timer = setTimeout(cancel, 15000);
    try {
      const response = await fetch(root + path, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
      const data = await response.json().catch(() => null);
      if (!response.ok || !data) {
        const error = new Error(data?.description || data?.message || config.copy.retry);
        error.status = response.status;
        throw error;
      }
      if (path === 'cart.js') bindCart(data);
      return data;
    } finally {
      clearTimeout(timer);
      options.signal?.removeEventListener('abort', cancel);
    }
  }
  const post = (path, data, signal) => request(path, {
    method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(data), signal
  });
  const qty = item => Math.max(0, Math.floor(Number(item.quantity) || 0));
  const on = value => ['true', '1', 'yes', 'on'].includes(String(value).toLowerCase());
  const bagFor = item => config.bags.find(bag => String(bag.id) === idOf(item));
  const isFree = item => isDock(item) && qty(item) > 0 && item.final_line_price === 0;
  const giftEligible = item => {
    const bag = bagFor(item), props = item.properties || {};
    return Boolean(bag?.preorder && bag.available && config.available && config.giftCheckoutEnabled && config.giftOfferEnabled
      && !on(props._m1_dock_gift_opt_out) && props._m1_dock_gift_offer !== 'off');
  };
  function allowance(cart) {
    const items = (cart.items || []).filter(Boolean), bags = items.filter(item => bagIds.has(idOf(item)));
    const docks = items.filter(isDock);
    const gift = bags.filter(giftEligible).reduce((sum, item) => sum + qty(item), 0);
    const stock = bags.filter(item => !bagFor(item)?.preorder && bagFor(item)?.available);
    const stockCount = stock.reduce((sum, item) => sum + qty(item), 0);
    const markedPaid = stock.filter(item => on(item.properties?._m1_dock_paid_addon));
    const markedCapacity = markedPaid.reduce((sum, item) => sum + qty(item), 0);
    // Old website carts did not put paid intent on the bag. Recognize their
    // explicit Dock selection only while its stock-bag attempt still exists.
    const legacyPaid = docks.filter(item => item.properties?._m1_dock_offer !== 'preorder-gift').reduce((sum, item) => {
      const token = item.properties?._m1_dock_attempt;
      if (!token) return sum + qty(item);
      const bag = stock.find(bag => bag.properties?._m1_dock_attempt === token || bag.key === item.parent_relationship?.parent_key);
      return sum + (bag && !on(bag.properties?._m1_dock_paid_addon) ? Math.min(qty(bag), qty(item)) : 0);
    }, 0);
    const paid = Math.min(stockCount, markedCapacity + legacyPaid);
    return { gift, paid, stock: stockCount, docks, bags, total: gift + paid };
  }
  function quantityLimit(item, cart) {
    if (!isDock(item)) return Infinity;
    const capacity = allowance(cart);
    const kind = item.properties?._m1_dock_offer === 'preorder-gift' ? 'gift' : 'paid';
    const used = capacity.docks.filter(other => other.key !== item.key
      && (other.properties?._m1_dock_offer === 'preorder-gift' ? 'gift' : 'paid') === kind)
      .reduce((sum, other) => sum + qty(other), 0);
    return Math.max(0, capacity[kind] - used);
  }
  function cartRepairs(cart) {
    const capacity = allowance(cart), updates = {};
    let gift = capacity.gift, paid = capacity.paid;
    for (const item of capacity.docks) {
      const freeIntent = item.properties?._m1_dock_offer === 'preorder-gift';
      const keep = Math.min(qty(item), freeIntent ? gift : paid);
      if (freeIntent) gift -= keep; else paid -= keep;
      if (keep !== qty(item)) updates[item.key] = keep;
    }
    return updates;
  }
  function giftIncreases(cart) {
    if (!config.giftCheckoutEnabled || !config.giftOfferEnabled || !config.available) return [];
    const capacity = allowance(cart);
    const inventory = config.inventoryManaged === false ? Infinity : Number(config.inventoryQuantity);
    if (!Number.isFinite(inventory) && inventory !== Infinity || inventory <= 0) return [];
    const current = capacity.docks.filter(isGift).reduce((sum, item) => sum + qty(item), 0);
    const remaining = Math.max(0, Math.floor(inventory) - capacity.docks.reduce((sum, item) => sum + qty(item), 0));
    const extra = Math.min(Math.max(0, capacity.gift - current), remaining);
    if (!extra) return [];
    const seed = capacity.docks.find(item => isGift(item) && !item.parent_relationship);
    const bag = capacity.bags.find(giftEligible);
    return [seed ? { key: seed.key, quantity: qty(seed) + extra, extra }
      : { quantity: extra, extra, properties: { _m1_dock_offer: 'preorder-gift',
        _m1_dock_attempt: bag?.properties?._m1_dock_attempt || ('gift-' + Date.now().toString(36)) } }];
  }
  function giftCodeUpdate(cart) {
    const code = String(config.giftDiscountCode || '').trim();
    if (!code) return null;
    if (!Array.isArray(cart.discount_codes)) return undefined; // Liquid seed requires an authoritative Ajax read.
    const codes = cart.discount_codes.map(entry => String(entry.code || '').trim()).filter(Boolean);
    const hasCode = codes.some(value => value.toLowerCase() === code.toLowerCase());
    const capacity = allowance(cart);
    const needsCode = Boolean(capacity.gift && capacity.docks.length);
    if (hasCode === needsCode) return null;
    return needsCode ? [...codes, code].join(',') : codes.filter(value => value.toLowerCase() !== code.toLowerCase()).join(',');
  }
  function cartGiftError(cart) {
    const capacity = allowance(cart);
    const giftUnits = Math.min(capacity.gift, capacity.docks.reduce((sum, item) => sum + qty(item), 0));
    if (!giftUnits) return capacity.docks.some(isGift);
    const code = String(config.giftDiscountCode || '').trim().toLowerCase();
    if (!code || !(cart.discount_codes || []).some(entry => entry.applicable && String(entry.code).toLowerCase() === code)) return true;
    let allocated = 0;
    for (const item of capacity.docks) {
      if (!Number.isSafeInteger(item.original_line_price) || !Number.isSafeInteger(item.final_line_price)
        || item.original_line_price < item.final_line_price || !Array.isArray(item.line_level_discount_allocations)) return true;
      for (const discount of item.line_level_discount_allocations) {
        const application = discount.discount_application || {};
        if (String(application.title || application.code || '').toLowerCase() === code) {
          if (!Number.isSafeInteger(discount.amount) || discount.amount < 0) return true;
          allocated += discount.amount;
        }
      }
    }
    // The dedicated code/title must account for every free unit across all
    // identical Dock lines. Shopify may allocate it to the paid-intent line.
    const unit = Number(config.price);
    return !Number.isSafeInteger(unit) || unit <= 0 || allocated !== giftUnits * unit;
  }
  function cartNeedsRepair(cart) {
    return Boolean(discountRestore || migration) || giftCodeUpdate(cart) !== null
      || (cart.items || []).some(item => bagFor(item)?.preorder && (!config.available || !config.giftOfferEnabled) && item.properties?._m1_dock_gift_offer !== 'off')
      || (cart.items || []).some(item => isDock(item) && item.parent_relationship?.parent_key)
      || Object.keys(cartRepairs(cart)).length > 0 || giftIncreases(cart).length > 0;
  }
  async function restoreDiscounts(cart) {
    if (!discountRestore) return cart;
    const expected = discountRestore;
    const matches = current => {
      const entries = current.discount_codes || [];
      return entries.length === expected.length && expected.every(before => entries.some(after =>
        String(after.code).toLowerCase() === before.code.toLowerCase() && (!before.applicable || after.applicable)));
    };
    if (!matches(cart)) {
      await post('cart/update.js', { discount: expected.map(entry => entry.code).join(',') });
      cart = await request('cart.js');
    }
    if (!matches(cart)) {
      const error = new Error(config.copy.giftDiscountConflict || config.copy.giftPricingError);
      error.cart = cart;
      throw error;
    }
    discountRestore = null;
    saveRecovery();
    return cart;
  }
  async function syncDiscount(cart) {
    const discount = giftCodeUpdate(cart);
    if (discount === null) return cart;
    if (discount === undefined) throw new Error(config.copy.retry);
    const original = cart.discount_codes.map(entry => ({ code: String(entry.code), applicable: Boolean(entry.applicable) })).filter(entry => entry.code);
    const otherApplied = original.filter(entry => entry.applicable && entry.code.toLowerCase() !== String(config.giftDiscountCode).trim().toLowerCase());
    discountRestore = original;
    saveRecovery(); // Persist before the write, including a lost response/reload.
    try {
      await post('cart/update.js', { discount });
      cart = await request('cart.js');
    } catch (failure) {
      cart = await request('cart.js');
      await restoreDiscounts(cart);
      throw failure;
    }
    if (otherApplied.some(before => !(cart.discount_codes || []).some(after => after.applicable
      && String(after.code).toLowerCase() === before.code.toLowerCase()))) {
      cart = await restoreDiscounts(cart);
      const error = new Error(config.copy.giftDiscountConflict || config.copy.giftPricingError);
      error.cart = cart;
      throw error;
    }
    discountRestore = null;
    saveRecovery();
    return cart;
  }
  async function migrateLegacy(cart) {
    // Native nesting cannot be changed in-place. Persist a Dock-only move and
    // read after every write so a lost response cannot duplicate a Dock.
    while (migration || cart.items.some(item => isDock(item) && item.parent_relationship?.parent_key)) {
      if (!migration) {
        const item = cart.items.find(item => isDock(item) && item.parent_relationship?.parent_key);
        const token = 'migrate-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2);
        migration = { key: item.key, parent: item.parent_relationship.parent_key, quantity: qty(item),
          properties: { ...item.properties, _m1_dock_migration: token }, token };
        saveRecovery();
      }
      const existing = cart.items.find(item => isDock(item) && item.properties?._m1_dock_migration === migration.token);
      const original = cart.items.find(item => item.key === migration.key);
      if (original) {
        await post('cart/change.js', { id: original.key, quantity: 0 });
        cart = await request('cart.js');
      }
      if (!existing && cart.items.some(item => bagIds.has(idOf(item)))) {
        let failure;
        try { await post('cart/add.js', { items: [{ id: Number(config.id), quantity: migration.quantity, properties: migration.properties }] }); }
        catch (error) { failure = error; }
        cart = await request('cart.js');
        if (!cart.items.some(item => isDock(item) && item.properties?._m1_dock_migration === migration.token)) throw failure || new Error(config.copy.retry);
      }
      migration = null;
      saveRecovery();
    }
    return cart;
  }
  async function closeUnavailableGiftOffer(cart) {
    if (config.available && config.giftOfferEnabled) return cart;
    const bags = cart.items.filter(item => bagFor(item)?.preorder && item.properties?._m1_dock_gift_offer !== 'off');
    for (const bag of bags) {
      await post('cart/change.js', { id: bag.key, quantity: qty(bag),
        properties: { ...bag.properties, _m1_dock_gift_offer: 'off' } });
    }
    return bags.length ? request('cart.js') : cart;
  }
  async function reconcileCart(cart) {
    if (readOnly() || !cartNeedsRepair(cart)) return cart;
    if (cartRepair) return cartRepair;
    cartRepair = (async () => {
      await freshConfig();
      let next = await request('cart.js');
      try {
        next = await restoreDiscounts(next);
        next = await closeUnavailableGiftOffer(next);
        const reductions = cartRepairs(next);
        if (Object.keys(reductions).length) next = await post('cart/update.js', { updates: reductions });
        next = await migrateLegacy(next);
        const increase = giftIncreases(next)[0];
        if (increase) {
          const beforeQuantity = next.items.filter(isGift).reduce((sum, item) => sum + qty(item), 0);
          let failure;
          try {
            if (increase.key) await post('cart/change.js', { id: increase.key, quantity: increase.quantity });
            else await post('cart/add.js', { items: [{ id: Number(config.id), quantity: increase.quantity, properties: increase.properties }] });
          } catch (error) { failure = error; }
          next = await request('cart.js');
          const afterQuantity = next.items.filter(isGift).reduce((sum, item) => sum + qty(item), 0);
          if (afterQuantity <= beforeQuantity || giftIncreases(next).length) throw failure || new Error(config.copy.giftQuantityError);
        }
        next = await syncDiscount(next);
        if (cartNeedsRepair(next)) throw new Error(config.copy.pairedOnly);
        return next;
      } catch (error) { error.cart ||= next; throw error; }
    })().finally(() => { cartRepair = null; });
    return cartRepair;
  }
  async function changeBagQuantity(cart, key, quantity) {
    if (readOnly()) throw new Error(config.copy.preview);
    if (cartChanging || purchaseBusy) throw new Error(config.copy.retry);
    cartChanging = true;
    syncCartGuards();
    try {
      if (cartRepair) await cartRepair;
      cart = await request('cart.js');
      const item = cart.items.find(item => item.key === key && bagFor(item));
      if (!item) return await reconcileCart(cart);
      quantity = Math.max(0, Math.floor(Number(quantity) || 0));
      if (quantity < qty(item)) {
        const projected = { ...cart, items: cart.items.map(line => line.key === key ? { ...line, quantity } : line).filter(line => qty(line) > 0) };
        cart = await post('cart/update.js', { updates: { [key]: quantity, ...cartRepairs(projected) } });
      } else if (quantity > qty(item)) {
        cart = await post('cart/change.js', { id: key, quantity });
      }
      cart = await reconcileCart(cart);
      if (cartGiftError(cart)) throw new Error(config.copy.giftPricingError);
      return cart;
    } catch (error) {
      if (!error.cart) {
        try { cart = await request('cart.js'); } catch {}
        error.cart = cart;
      }
      throw error;
    } finally {
      cartChanging = false;
      syncCartGuards();
    }
  }
  async function changeDockQuantity(cart, key, quantity) {
    if (readOnly()) throw new Error(config.copy.preview);
    if (cartRepair) await cartRepair;
    cart = await request('cart.js');
    try {
      const item = cart.items.find(item => item.key === key && isDock(item));
      if (!item) return reconcileCart(cart);
      quantity = Math.max(0, Math.floor(Number(quantity) || 0));
      if (quantity === qty(item)) return cart;
      // Gift quantity follows the bag. The UI exposes removal only for a
      // charged Dock, and never offers independent Dock quantity steppers.
      if (quantity !== 0 || isFree(item)) throw new Error(config.copy.pairedOnly);
      const code = String(config.giftDiscountCode || '').trim().toLowerCase();
      const giftAllocation = (item.line_level_discount_allocations || []).reduce((sum, discount) => {
        const application = discount.discount_application || {};
        return sum + (String(application.title || application.code || '').toLowerCase() === code ? Number(discount.amount) || 0 : 0);
      }, 0);
      const freeUnits = giftAllocation / Number(config.price);
      if (!Number.isInteger(freeUnits) || freeUnits < 0 || freeUnits > qty(item)) throw new Error(config.copy.giftPricingError);
      const paidLines = cart.items.filter(line => isDock(line) && !isGift(line));
      let remove = Math.min(qty(item) - freeUnits, paidLines.reduce((sum, line) => sum + qty(line), 0));
      if (!remove) throw new Error(config.copy.giftPricingError);
      const updates = {};
      // Allocation is independent of purchase intent. A charged gift-marked
      // line may represent the optional paid unit; remove that paid allowance
      // from its provenance line, then let Shopify allocate the free discount.
      paidLines.sort((a, b) => Number(b.key === key) - Number(a.key === key));
      for (const line of paidLines) {
        const count = Math.min(qty(line), remove);
        if (count) updates[line.key] = qty(line) - count;
        remove -= count;
      }
      await post('cart/update.js', { updates });
      cart = await request('cart.js');
      const remainingPaid = cart.items.filter(line => isDock(line) && !isGift(line));
      const optedBags = cart.items.filter(line => bagIds.has(idOf(line)) && on(line.properties?._m1_dock_paid_addon));
      for (const bag of optedBags) {
        const token = bag.properties?._m1_dock_attempt;
        if (remainingPaid.some(line => token && line.properties?._m1_dock_attempt === token)) continue;
        await post('cart/change.js', { id: bag.key, quantity: qty(bag), properties: { ...bag.properties, _m1_dock_paid_addon: 'false' } });
      }
      return await reconcileCart(await request('cart.js'));
    } catch (error) { error.cart ||= cart; throw error; }
  }
  async function freshConfig(signal) {
    const controller = new AbortController();
    const cancel = () => controller.abort();
    signal?.addEventListener('abort', cancel, { once: true });
    if (signal?.aborted) controller.abort();
    const timeout = setTimeout(cancel, 10000);
    try {
      const response = await fetch(root + '?section_id=m1-dock-availability', {
        credentials: 'same-origin', cache: 'no-store', signal: controller.signal
      });
      if (!response.ok) throw new Error(config.copy.retry);
      const fresh = new DOMParser().parseFromString(await response.text(), 'text/html');
      const truth = JSON.parse(fresh.querySelector('[data-m1-dock-config]').textContent);
      applyConfig(truth);
      return truth;
    } finally {
      clearTimeout(timeout);
      signal?.removeEventListener('abort', cancel);
    }
  }
  function applyConfig(next) {
    for (const key of ['available', 'price', 'inventoryManaged', 'inventoryQuantity', 'giftPool', 'giftOfferEnabled', 'giftCheckoutEnabled', 'giftDiscountCode', 'bags']) {
      if (key in next) config[key] = next[key];
    }
    bagIds.clear();
    config.bags.forEach(bag => { if (bag.id) bagIds.add(String(bag.id)); });
    if (!config.available) checked = false;
    document.dispatchEvent(new CustomEvent('nex:dock-config-updated', { detail: { config } }));
  }
  function syncWallet(surface) {
    // Both accelerated and regular buttons use the same verified cart path.
    // Keep the native wallet mounted, but never create nested additional items.
    surface.querySelectorAll('[data-m1-dock-wallet-fields]').forEach(fields => fields.remove());
  }
  function sync() {
    const currentSurfaces = surfaces();
    if (checked && !currentSurfaces.some(surface => selectionFor(surface))) {
      checked = false;
      checkedVariant = '';
    }
    document.querySelectorAll('[data-m1-dock-row]').forEach(row => {
      const surface = surfaceFor(row);
      const canAdd = Boolean(surface && eligible(surface));
      const checkbox = row.querySelector('[data-m1-dock-checkbox]');
      if (!checkbox) return;
      row.hidden = !canAdd;
      checkbox.checked = canAdd && selectionFor(surface);
      checkbox.disabled = !canAdd || purchaseBusy || Boolean(surface?._checkoutAttempt);
      const price = row.querySelector('[data-m1-dock-price]');
      if (price) price.textContent = '+' + money(config.price);
    });
    for (const surface of currentSurfaces) {
      syncWallet(surface);
      const card = selectedCard(surface);
      const button = surface.querySelector('.gdab__confirm, .hero-purchase__checkout');
      if (button && card && !surface._checkoutAttempt) {
        const baseLabel = card.dataset.checkoutLabel || config.copy.checkout;
        button.textContent = baseLabel;
      }
      surface.queueShopPayCheck?.();
    }
    const anyBag = currentSurfaces.some(surface => [...surface.querySelectorAll('.gdab__card, .hero-purchase__color')]
      .some(card => card.dataset.inventoryAvailable === 'true'));
    if (purchaseBusy) document.querySelectorAll('.gdab__buy, .hero-purchase__buy').forEach(shell => { shell.inert = true; });
    document.querySelectorAll('[data-m1-dock-section]:not([data-preorder-gift-section])').forEach(section => {
      section.hidden = !dockAvailable() || !anyBag;
    });
  }
  function applyPreview(surface) {
    if (!preview || !config.draft) return;
    for (const card of surface.querySelectorAll('.gdab__card, .hero-purchase__color')) {
      if (!originals.has(card)) originals.set(card, { ...card.dataset });
      const original = originals.get(card);
      const state = card.dataset.color === preview.color ? preview.status : (preview.otherStatus || (original.inventoryAvailable !== 'true' ? 'soldout' : original.preorder === 'true' ? 'preorder' : 'stock'));
      const available = state !== 'soldout';
      const pre = state.startsWith('preorder');
      if (card.dataset.inventoryAvailable !== String(available)) card.dataset.inventoryAvailable = String(available);
      if (card.dataset.preorder !== String(pre)) card.dataset.preorder = String(pre);
      card.dataset.sellState = pre ? 'preorder' : 'stock';
      card.dataset.mode = pre ? config.copy.preorder : 'Available now';
      card.dataset.checkoutLabel = pre ? (original.preorder === 'true' ? original.checkoutLabel : config.copy.preorder) : config.copy.checkout;
      // The preview retains actual prices; it never pretends a lower price is purchasable.
    }
  }
  function setPreview(value) {
    if (!config.draft) return false;
    if (value && (!['black', 'orange'].includes(value.color)
      || !['stock', 'stock-low', 'preorder', 'preorder-low', 'soldout'].includes(value.status)
      || !['instock', 'low', 'soldout'].includes(value.dock))) return false;
    preview = value ? { ...value } : null;
    checked = false;
    checkedVariant = '';
    for (const surface of document.querySelectorAll('nex-gdab, nex-hero-purchase')) {
      if (!preview) {
        surface.querySelectorAll('.gdab__card, .hero-purchase__color').forEach(card => {
          const original = originals.get(card);
          if (original) Object.assign(card.dataset, original);
        });
      } else applyPreview(surface);
      const card = surface.querySelector(`[data-color="${preview?.color || 'black'}"][data-variant-id]`);
      if (card) {
        if (surface.matches('nex-gdab')) surface.selectCard?.(card, { silent: true, animate: false });
        else surface.select?.(card, { announce: false, syncDock: false, animate: false });
      }
    }
    document.documentElement.toggleAttribute('data-m1-dock-preview', Boolean(preview));
    sync();
    return true;
  }
  async function checkout(surface, { shopPay = false } = {}) {
    if (readOnly()) { showError(surface, config.copy.preview); return; }
    if (surface._checkoutAttempt || purchaseBusy || cartChanging) return;
    const card = selectedCard(surface);
    if (!card || card.dataset.inventoryAvailable !== 'true') return;
    const bagId = card.dataset.variantId;
    if (!bagIds.has(bagId)) return;
    const requestedGift = giftFor(surface);
    const requestedPaid = selectionFor(surface);
    const button = surface.querySelector('.gdab__confirm, .hero-purchase__checkout');
    if (!button) return;
    const attempt = { label: button.textContent, controller: new AbortController(), navigating: false };
    purchaseBusy = true;
    surface._checkoutAttempt = attempt;
    surface.beginCartMutation?.();
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = config.copy.opening;
    document.querySelectorAll('.gdab__card, .hero-purchase__color').forEach(node => node.disabled = true);
    sync();
    try {
      const current = () => surface._checkoutAttempt === attempt && surface.isConnected && !readOnly();
      const signal = attempt.controller.signal;
      if (cartRepair) await cartRepair;
      if (!current()) return;
      let cart = await request('cart.js', { signal });
      if (!current()) return;
      {
        const truth = await freshConfig(signal);
        if (!current()) return;
        const bag = truth.bags.find(item => String(item.id) === bagId);
        if (!bag?.available) throw new Error(config.copy.giftChanged || config.copy.retry);
        if ((requestedGift || requestedPaid) && !truth.available) {
          checked = false;
          throw new Error(config.copy.unavailable);
        }
        if (requestedGift && !giftFor(surface)) throw new Error(config.copy.giftChanged || config.copy.retry);
        if (requestedPaid && bag.preorder) { checked = false; throw new Error(config.copy.giftChanged || config.copy.retry); }
      }
      const kind = requestedGift ? 'preorder-gift' : requestedPaid ? 'paid-addon' : '';
      if (pendingPurchase) {
        const old = pendingPurchase;
        const remove = cart.items.filter(item => item.properties?._m1_dock_attempt === old.token
          && (old.bagId !== bagId || (isDock(item) && item.properties?._m1_dock_offer !== kind)));
        if (remove.length) cart = await post('cart/update.js', { updates: Object.fromEntries(remove.map(item => [item.key, 0])) }, signal);
        if (!current()) return;
        if (old.bagId !== bagId) pendingPurchase = null;
        saveRecovery();
      }
      if (!pendingPurchase) pendingPurchase = { bagId, token: `dock-${Date.now()}-${Math.random().toString(36).slice(2)}` };
      saveRecovery();
      const pending = pendingPurchase;
      const findLine = id => cart.items.find(item => idOf(item) === id && item.properties?._m1_dock_attempt === pending.token);
      const addAndRead = async item => {
        let failure;
        try { await post('cart/add.js', { items: [item] }, signal); } catch (error) { failure = error; }
        cart = await request('cart.js', { signal });
        const found = findLine(String(item.id));
        if (!found) throw failure || new Error(config.copy.retry);
        return found;
      };
      let parent = findLine(bagId);
      if (!parent) {
        const bag = config.bags.find(item => String(item.id) === bagId);
        const businessGift = Boolean(bag?.preorder && config.giftOfferEnabled && config.available);
        parent = await addAndRead({ id: Number(bagId), quantity: 1, properties: {
          _m1_dock_attempt: pending.token,
          _m1_dock_gift_offer: businessGift ? 'on' : 'off',
          _m1_dock_paid_addon: requestedPaid ? 'true' : 'false'
        } });
      }
      if (!current()) return;
      if (kind && !findLine(config.id)) {
        await addAndRead({ id: Number(config.id), quantity: 1,
          properties: { _m1_dock_attempt: pending.token, _m1_dock_offer: kind } });
      }
      if (!current()) return;
      cart = await reconcileCart(cart);
      parent = findLine(bagId);
      const child = findLine(config.id);
      if (!parent || (kind && !child)) throw new Error(config.copy.retry);
      if (cartGiftError(cart)) throw new Error(config.copy.giftPricingError || config.copy.retry);
      if (!current()) return;
      attempt.navigating = true;
      pendingPurchase = null;
      saveRecovery();
      const checkoutUrl = surface.cfg?.checkoutUrl || surface.dataset.checkoutUrl || root + 'checkout';
      location.assign(checkoutUrl + (shopPay ? (checkoutUrl.includes('?') ? '&' : '?') + 'payment=shop_pay' : ''));
    } catch (error) {
      if (surface._checkoutAttempt === attempt) {
        surface.resetCheckout?.();
        showError(surface, error.message || config.copy.retry);
      }
    } finally {
      purchaseBusy = false;
      document.querySelectorAll('.gdab__card, .hero-purchase__color').forEach(node => node.disabled = false);
      surface.endCartMutation?.();
      sync();
      syncCartGuards();
    }
  }
  function attach() {
    host = document.querySelector('nex-gdab:not([data-gdab-duplicate])');
    observer?.disconnect();
    observer = new MutationObserver(sync);
    const observeOptions = { attributes: true, subtree: true, attributeFilter: ['data-gdab-state', 'aria-checked', 'data-preorder-future', 'data-inventory-available'] };
    surfaces().forEach(surface => observer.observe(surface, observeOptions));
    sync();
  }
  const api = window.NexDock = {
    isDock, isGift, isFree, isBag: item => Boolean(bagFor(item)), quantityLimit, changeBagQuantity, changeDockQuantity, giftFor, cartNeedsRepair, cartGiftError, reconcileCart, checkout, sync, applyPreview, setPreview,
    get readOnly() { return readOnly(); },
    get preview() { return preview; }, get busy() { return purchaseBusy || cartChanging; }, get selected() { return checked; },
    get config() { return config; },
    handlesPurchase(surface) { return Boolean(preview || purchaseBusy || bagIds.has(selectedCard(surface)?.dataset.variantId)); },
    clearSelection() { checked = false; checkedVariant = ''; sync(); }
  };
  const syncCartGuards = () => document.querySelectorAll('nex-cart-inventory').forEach(guard => guard.sync?.());
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncCartGuards, { once: true });
  else syncCartGuards();
  document.addEventListener('nex:inventory-updated', sync);
  document.addEventListener('nex:inventory-document', event => {
    try {
      const next = JSON.parse(event.detail.document.querySelector('[data-m1-dock-config]').textContent);
      applyConfig(next);
      sync();
    } catch { /* Keep the last confirmed state if refresh fails. Cart API remains authoritative. */ }
  });
  document.addEventListener('nex:dock-open-m1', event => {
    if (!host) return;
    checked = false;
    checkedVariant = '';
    host.lastFocus = event.detail?.trigger || document.activeElement;
    host.openPanel(); sync();
  });
  document.addEventListener('shopify:section:load', attach);
  document.addEventListener('click', event => {
    const shell = event.target.closest?.('.gdab__buy, .hero-purchase__buy');
    const surface = shell && surfaceFor(shell);
    if (!surface || (!readOnly() && !bagIds.has(selectedCard(surface)?.dataset.variantId))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (readOnly()) showError(surface, config.copy.preview);
    else checkout(surface, { shopPay: true });
  }, true);
  document.addEventListener('submit', event => {
    if (!event.target.matches?.('.gdab__buy-form, .hero-purchase__buy-form')) return;
    const surface = surfaceFor(event.target);
    if (!surface || (!readOnly() && !bagIds.has(selectedCard(surface)?.dataset.variantId))) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (readOnly()) showError(surface, config.copy.preview);
    else checkout(surface, { shopPay: true });
  }, true);
  document.addEventListener('change', event => {
    const checkbox = event.target.closest('[data-m1-dock-checkbox]');
    if (!checkbox) return;
    const surface = surfaceFor(checkbox);
    checked = checkbox.checked && eligible(surface);
    checkedVariant = checked ? selectedCard(surface).dataset.variantId : '';
    sync();
  });
  window.addEventListener('pageshow', () => { sync(); });
  customElements.whenDefined('nex-gdab').then(() => {
    attach();
    document.dispatchEvent(new CustomEvent('nex:dock-ready'));
  });
})();

